import React, { useEffect, useState } from 'react';
import { History } from 'lucide-react';
import API from '../api';
import { groupActivities } from '../utils/activities';
import WaterLoader from './WaterLoader';

// Works already carried out on a water body (previously approved NOCs).
// Shown for information only — the tank is NOT frozen because of them.
// Used on tank selection (applicant) and the application review page (all roles).
const PreviousWorks = ({ tankId, excludeApplicationId, compact = false }) => {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!tankId) return;
    let cancelled = false;
    const params = excludeApplicationId ? { exclude: excludeApplicationId } : {};
    API.get(`/tanks/${tankId}/previous-works`, { params, silent: true })
      .then((r) => { if (!cancelled) setRows(Array.isArray(r?.data?.data) ? r.data.data : []); })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [tankId, excludeApplicationId]);

  if (rows === null) {
    return <WaterLoader size={48} label="Checking previous works on this water body…" className="py-4" />;
  }

  const fmt = (v) => (v ? new Date(v).toLocaleDateString('en-IN') : '—');

  return (
    <div className={`border border-amber-200 bg-amber-50 ${compact ? '' : 'mb-5'}`}>
      <div className="px-4 py-2.5 border-b border-amber-200 flex items-center gap-2">
        <History className="w-4 h-4 text-amber-700" />
        <h3 className="text-sm font-semibold text-amber-900">
          Previous Works on this Water Body {rows.length > 0 && `(${rows.length})`}
        </h3>
      </div>

      {rows.length === 0 ? (
        <p className="px-4 py-3 text-sm text-slate-600">No previous renovation works recorded for this water body.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-amber-600 text-white text-left text-xs uppercase tracking-wide">
                  <th className="px-3 py-2 font-semibold">NOC ID</th>
                  <th className="px-3 py-2 font-semibold">Organization / Applicant</th>
                  <th className="px-3 py-2 font-semibold">Works Carried Out</th>
                  <th className="px-3 py-2 font-semibold">Approved On</th>
                  <th className="px-3 py-2 font-semibold">Completion</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const groups = groupActivities(r.activity_ids);
                  return (
                    <tr key={r.application_id} className="odd:bg-white even:bg-amber-50 border-b border-amber-100 align-top">
                      <td className="px-3 py-2 font-semibold text-slate-800 whitespace-nowrap">
                        NOC{String(r.application_id).padStart(3, '0')}
                      </td>
                      <td className="px-3 py-2 text-slate-700">{r.organization_name || '—'}</td>
                      <td className="px-3 py-2 text-slate-700">
                        {Object.keys(groups).length === 0 ? '—' : (
                          <ul className="space-y-0.5">
                            {Object.entries(groups).map(([cat, subs]) => (
                              <li key={cat}>
                                <span className="font-medium">{cat}</span>
                                {subs.length > 0 && <span className="text-slate-500"> — {subs.join(', ')}</span>}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmt(r.approved_on)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {r.completion_issued_at
                          ? <span className="px-2 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-800 border-l-4 border-emerald-600">Completed {fmt(r.completion_issued_at)}</span>
                          : <span className="px-2 py-0.5 text-xs font-semibold bg-orange-100 text-orange-800 border-l-4 border-orange-500">In progress</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="px-4 py-2 text-xs text-amber-900">
            For information only — a new application can still be submitted for this water body.
          </p>
        </>
      )}
    </div>
  );
};

export default PreviousWorks;
